import { useNavigate, useParams } from 'react-router-dom';
import { Icon, SlotKey } from '@etnas/design-system';
import { useQueries, useQuery } from '@tanstack/react-query';
import { breakGlassApi, patientsApi } from '@/api';
import { usePatientSlots } from '../patientSlots';
import { useAuth } from '../auth';
import { patientHome } from '../workspaces';
import { shortName } from '@/lib/patient';

/**
 * Patient slots (zone 4): DS `ex-ptabs` markup with select + close. Pencil = unsaved work; shield = break-glass.
 * Keyboard: Alt+1..4 switches slots (registered in the shell).
 */
export function SlotTabs() {
  const { slots, closeSlot, dirtyFor } = usePatientSlots();
  const { me } = useAuth();
  const nav = useNavigate();
  const params = useParams();
  const activeId = params.patientId;
  const bg = useQuery({ queryKey: ['break-glass', 'active'], queryFn: breakGlassApi.active, enabled: slots.length > 0 && !!me?.tenant && !!(me?.permissions.includes('break_glass.start') || me?.permissions.includes('breakglass.start')), refetchInterval: 30_000 });
  const pats = useQueries({
    queries: slots.map((s) => ({ queryKey: ['patient', s.patientId], queryFn: () => patientsApi.get(s.patientId), enabled: !s.display, staleTime: 60_000 })),
  });
  if (!slots.length) return null;
  const role = me?.context.role.key;
  return (
    <nav className="ex-ptabs" aria-label="Open patients">
      {slots.map((s, i) => {
        const name = s.display ?? pats[i]?.data?.data.name.display ?? 'Loading…';
        const active = s.patientId === activeId;
        const dirty = dirtyFor(s.patientId).length > 0;
        const glass = bg.data?.items.some((g) => g.patientId === s.patientId);
        return (
          <span key={s.slot} className={`ex-ptab ex-slot-${s.slot}${active ? ' is-active' : ''}`} style={{ display: 'inline-flex' }}>
            <button
              type="button"
              className="ex-rowbtn"
              style={{ gap: 6, width: 'auto' }}
              aria-current={active ? 'page' : undefined}
              onClick={() => role && nav(patientHome(role, s.patientId, me?.context.roleId) ?? '/')}
              aria-label={`Slot ${'ABCD'[s.slot - 1]}: ${name}${dirty ? ', unsaved work' : ''}${glass ? ', emergency access active' : ''}`}
            >
              <SlotKey slot={s.slot} />
              <span className="ex-ptab-name">{shortName(name)}</span>
              {s.meta ? <span className="ex-ptab-meta ex-mono">{s.meta}</span> : null}
              {dirty ? (
                <span className="ex-ptab-dirty" title="Unsaved work in this chart">
                  <Icon name="edit" size={12} />
                </span>
              ) : null}
              {glass ? <Icon name="shield" size={12} className="ex-fg-critical" title="Break-glass access active" /> : null}
            </button>
            <button type="button" className="ex-ptab-close" onClick={() => closeSlot(s.slot)} aria-label={`Close ${name}`}>
              <Icon name="x" size={12} />
            </button>
          </span>
        );
      })}
    </nav>
  );
}
